import { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axiosClient from "../axiosClient";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useStateContext } from "../context/ContextProvider";

const ClientComment = ({ id, open, handleClose, getUsers }) => {
  const [comment, setComment] = useState("");
  const [visibility, setVisibility] = useState(true);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState("");
  const { user } = useStateContext();

  // Memoize fetchComments to prevent unnecessary re-renders
  const fetchComments = useCallback(async () => {
    try {
      const response = await axiosClient.get(`api/client-comments/create/`, {
        params: { user_id: user.id },
      });
      console.log("Fetched comments:", response.data); // Debug: Log fetched comments
      setComments(response.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  }, [user.id]);

  const validateForm = () => {
    if (!comment.trim()) {
      setError("Comment is required");
      return false;
    }
    setError("");
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        comment_text: comment,
        visibility: visibility,
      };
      console.log("Saving comment with payload:", payload); // Debug: Log payload
      const response = await axiosClient.post("api/client-comments/create/", payload);
      console.log("Comment saved:", response.data); // Debug: Log saved comment
      fetchComments(); // Refresh comments after saving
      setComment("");
      setVisibility(true);
    } catch (error) {
      if (error.response) {
        console.error("Error response:", error.response);
        alert(`Error saving comment: ${JSON.stringify(error.response.data)}`);
      } else {
        console.error("Error saving comment:", error);
        alert("Error saving comment: Network error or server unreachable");
      }
    }
  };

  useEffect(() => {
    if (open) {
      fetchComments();
    }
  }, [open, fetchComments]);

  // Handle modal close events
  const handleModalClose = (event, reason) => {
    // Prevent closing on backdrop click or Escape key press
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setComment("");
    setError("");
    setVisibility(true);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      disableEscapeKeyDown
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "80%", md: 800 },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: { xs: 2, sm: 4 },
          overflowY: "auto",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              id="modal-modal-title"
              sx={{ fontSize: { xs: "16px", sm: "18px" }, fontWeight: "500" }}
              variant="h4"
              component="h2"
            >
              Comment
            </Typography>
            <Typography
              onClick={handleModalClose}
              sx={{ cursor: "pointer" }}
              id="modal-modal-title"
              variant="h6"
              component="h2"
            >
              <CloseOutlinedIcon fontSize="medium" />
            </Typography>
          </Box>
          <List>
            {comments.map((comment) => (
              <ListItem key={comment.id}>
                <Typography
                  variant="body1"
                  sx={{ fontSize: { xs: "14px", sm: "16px" } }}
                >
                  <i>
                    {comment.user?.username || "Unknown User"} -{" "}
                    {new Date(comment.created_at).toLocaleString()}
                  </i>{" "}
                  - {comment.visibility ? "visible" : ""}
                  <br />
                  {comment.comment_text}
                </Typography>
                <Divider />
              </ListItem>
            ))}
          </List>
        </Box>
        <Typography
          variant="h6"
          component="h6"
          sx={{ fontSize: { xs: "14px", sm: "16px" }, mt: 2 }}
        >
          Add Comment
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={visibility}
              onChange={(e) => setVisibility(e.target.checked)}
              size="small"
            />
          }
          label="View By Client"
          sx={{ fontSize: { xs: "14px", sm: "16px" } }}
        />
        <TextField
          fullWidth
          multiline
          rows={4}
          value={comment}
          variant="outlined"
          onChange={(e) => {
            setComment(e.target.value);
            if (e.target.value.trim()) setError("");
          }}
          placeholder="Enter your comment"
          sx={{ my: 2, fontSize: { xs: "14px", sm: "16px" } }}
          error={!!error}
          helperText={error}
        />
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            padding: { xs: "8px 16px", sm: "10px 20px" },
          }}
          disabled={!comment.trim()}
        >
          Save
        </Button>
      </Box>
    </Modal>
  );
};

export default ClientComment;